import type { Measure } from '../../../types/bi';

export type AliasPolicyMode = 'auto' | 'manual';

function normalizeAliasInput(value: string | undefined): string {
  return (value || '').trim();
}

function normalizeFieldToken(fieldKey: string | undefined): string {
  const raw = (fieldKey || '')
    .replace(/^field_key:/, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return raw || 'value';
}

export function buildDefaultMeasureAlias(
  measure: Pick<Measure, 'field_key' | 'aggregation'>,
): string {
  const fieldToken = normalizeFieldToken(measure.field_key);
  return `${measure.aggregation}_${fieldToken}`;
}

function uniqueAlias(candidate: string, used: Set<string>): string {
  const base = candidate || 'measure';
  let next = base;
  let suffix = 2;

  while (used.has(next.toLowerCase())) {
    next = `${base}_${suffix}`;
    suffix += 1;
  }

  used.add(next.toLowerCase());
  return next;
}

/**
 * Applies deterministic alias generation and duplicate prevention.
 * - auto mode: always regenerate aliases from aggregation + field_key.
 * - manual mode: preserve provided alias when present, fallback to generated.
 */
export function applyMeasureAliasPolicy(
  measures: Measure[],
  mode: AliasPolicyMode = 'manual',
): Measure[] {
  const used = new Set<string>();

  return measures.map((measure) => {
    const generated = buildDefaultMeasureAlias(measure);
    const preferred = mode === 'auto' ? generated : normalizeAliasInput(measure.alias) || generated;

    return {
      ...measure,
      alias: uniqueAlias(preferred, used),
    };
  });
}
